/**
 * 同步管理器 — Sync Manager
 * ============================================================
 * 职责：
 *   1. 管理数据同步状态机（unsaved → saving → synced → ...）
 *   2. 提供适配器接口，今日 localStorage，明天换一行代码对接云端
 *   3. 驱动右上角同步指示器 UI（syncStatus 标签 + 状态点）
 *
 * 适配器契约（Adapter Interface）：
 *   { save(key, data) → Promise<void>, load(key) → Promise<?> }
 *
 * 使用示例（迁移到云端时）：
 *   import { CloudflareAdapter } from './adapters/cloudflare.js';
 *   setAdapter(new CloudflareAdapter());
 *   其余代码一行不改。
 * ============================================================
 */
import { getSessionUser } from './modules/auth.js';

// 云端模式开关：设为 true 时使用 Cloudflare 后端适配器
// （本地仍走 localStorage，不影响现状）
export const CLOUD_MODE = true;

// ==================== 状态常量 ====================
const STATUS = {
  UNSAVED:  'unsaved',
  SAVING:   'saving',
  SYNCED:   'synced',
  ERROR:    'error',
};

const STATUS_LABEL = {
  [STATUS.UNSAVED]: '待保存',
  [STATUS.SAVING]:  '同步中…',
  [STATUS.SYNCED]:  '已同步',
  [STATUS.ERROR]:   '同步失败',
};

// ==================== 本地存储适配器（默认） ====================
const LOCAL_ADAPTER = {
  save(key, payload) {
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  },
  load(key) {
    try {
      const raw = localStorage.getItem(key);
      return Promise.resolve(raw ? JSON.parse(raw) : null);
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

// ==================== 同步管理器 ====================
let _adapter = LOCAL_ADAPTER;
let _status = STATUS.SYNCED;          // 初始状态（页面加载完数据都是已有的）
let _timer = null;
let _pendingData = null;              // 待保存的数据快照
let _listeners = [];                  // 状态变更回调
let _debounceMs = 800;                // 防抖延迟

// DOM 缓存
let _statusEl = null;
let _dotEl = null;

function getEls() {
  if (!_statusEl) _statusEl = document.getElementById('syncStatus');
  if (!_dotEl) _dotEl = document.getElementById('syncDot');
  return { statusEl: _statusEl, dotEl: _dotEl };
}

function applyUI(status) {
  const { statusEl, dotEl } = getEls();
  if (statusEl) {
    statusEl.textContent = STATUS_LABEL[status] || status;
    statusEl.className = 'text-[10px] font-black tracking-widest uppercase ' +
      (status === STATUS.SYNCED ? 'text-tertiary' :
       status === STATUS.SAVING  ? 'text-amber-500' :
       status === STATUS.ERROR   ? 'text-red-400' :
                                   'text-slate-400');
  }
  if (dotEl) {
    dotEl.className = 'w-2 h-2 rounded-full ' +
      (status === STATUS.SYNCED ? 'bg-tertiary shadow-[0_0_8px_#60ec79]' :
       status === STATUS.SAVING ? 'bg-amber-400 shadow-[0_0_8px_#fbbf24] animate-pulse' :
       status === STATUS.ERROR  ? 'bg-red-400 shadow-[0_0_8px_#f87171]' :
                                  'bg-slate-300');
  }
}

function setStatus(status) {
  if (_status === status) return;
  _status = status;
  applyUI(status);
  _listeners.forEach(fn => { try { fn(status); } catch(_) {} });
}

// ==================== 公开 API ====================

/** 切换适配器（迁移云端时只需调这个） */
export function setAdapter(adapter) {
  _adapter = adapter;
}

/** 标记数据有变动（用户在输入），开始防抖计时 */
export function markDirty() {
  if (_status === STATUS.UNSAVED) return; // 已经在未保存状态
  if (_status === STATUS.SAVING) return;  // 正在保存中，不中断
  setStatus(STATUS.UNSAVED);
}

/**
 * 触发保存（防抖），传入待保存的数据
 * 建议 debounce 800ms，用户停止输入后自动保存
 */
export function scheduleSave(data) {
  _pendingData = data;
  if (_status === STATUS.UNSAVED) {
    setStatus(STATUS.UNSAVED); // 保持 unsaved
  }
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    flushSave();
  }, _debounceMs);
}

/** 立即执行保存（不管防抖） */
export function flushSave() {
  if (_timer) { clearTimeout(_timer); _timer = null; }
  if (!_pendingData) return Promise.resolve();

  const data = _pendingData;
  _pendingData = null;
  setStatus(STATUS.SAVING);

  // 运行期撤销快照（_trash）不持久化：避免已删除档案的完整快照被上传云端/写入本地
  const payload = Object.assign({}, data);
  delete payload._trash;

  // 云端模式：整份数据一次推送（token 鉴权隔离）
  if (CLOUD_MODE) {
    return _adapter.save('cloud', payload)
      .then(() => {
        // 云端回写的乐观锁版本号同步回共享数据（保存的是 payload 拷贝）
        if (payload.updatedAt) data.updatedAt = payload.updatedAt;
        setStatus(STATUS.SYNCED);
      })
      .catch(err => {
        console.error('[SyncManager] cloud save failed', err);
        setStatus(STATUS.ERROR);
        setTimeout(() => { if (_status === STATUS.ERROR) setStatus(STATUS.UNSAVED); }, 3000);
      });
  }

  // 本地模式：构造所有需要保存的 key
  const session = getSessionUser();
  const keys = ['competitorTracker'];
  if (session && session.email) {
    const userKey = 'competitorTracker_' + session.email.replace(/[^a-zA-Z0-9@._-]/g, '');
    keys.push(userKey);
  }

  const tasks = keys.map(key => _adapter.save(key, payload));
  return Promise.all(tasks)
    .then(() => {
      setStatus(STATUS.SYNCED);
    })
    .catch(err => {
      console.error('[SyncManager] save failed', err);
      setStatus(STATUS.ERROR);
      // 3秒后自动重置为未保存（让用户知道有问题）
      setTimeout(() => {
        if (_status === STATUS.ERROR) setStatus(STATUS.UNSAVED);
      }, 3000);
    });
}

/** 手动强制保存当前数据 */
export function forceSave(data) {
  _pendingData = data;
  return flushSave();
}

/** 获取当前同步状态 */
export function getStatus() {
  return _status;
}

/** 注册状态变更监听 */
export function onStatusChange(fn) {
  _listeners.push(fn);
  // 立即回传当前状态
  try { fn(_status); } catch(_) {}
}

/** 初始化同步管理器（绑定 UI 元素） */
export function initSyncManager() {
  // 云端模式：切换适配器为 Cloudflare
  if (CLOUD_MODE) {
    // 延迟 import 避免未使用时的打包成本
    import('./adapters/cloudflare.js').then(function(mod) {
      _adapter = mod.CloudflareAdapter;
      console.info('[SyncManager] Cloudflare 云端适配器已启用');
    }).catch(function(e) {
      console.error('[SyncManager] 云端适配器加载失败', e);
    });
  }
  // 初始状态为已同步（页面加载时数据已就绪）
  setStatus(STATUS.SYNCED);
}

/** 运行时切换云端模式（供后续接入使用） */
export function setCloudMode(on) {
  if (on && !CLOUD_MODE) {
    return import('./adapters/cloudflare.js').then(function(mod) {
      _adapter = mod.CloudflareAdapter;
      console.info('[SyncManager] Cloudflare 云端适配器已启用');
    });
  }
  return Promise.resolve();
}

/** 设置防抖延迟（毫秒），默认 800 */
export function setDebounce(ms) {
  _debounceMs = ms;
}
